import { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";

const DRAWER_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const DRAWER_DURATION = "280ms";
const ACCENT = "#E8A317";

const BrandMark = ({ onClick, expanded }) => (
  <Tooltip title="Voltar aos módulos" placement="right">
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label="Ir para módulos"
      sx={{
        width: 40,
        height: 40,
        p: 0,
        border: "none",
        borderRadius: "50%",
        cursor: "pointer",
        flexShrink: 0,
        overflow: "hidden",
        bgcolor: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `0 6px 16px ${alpha("#3B2416", 0.22)}`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "scale(1.05)",
          boxShadow: `0 8px 20px ${alpha("#3B2416", 0.3)}`,
        },
        "&:active": { transform: "scale(0.96)" },
        "& img": {
          width: expanded ? 34 : 30,
          height: expanded ? 34 : 30,
          objectFit: "contain",
          display: "block",
          pointerEvents: "none",
        },
      }}
    >
      <img src="/Images/LogoPalmontMark.png" alt="Logo Palmont" />
    </Box>
  </Tooltip>
);

const NavLeaf = ({ item, Icon, expanded, onNavigate, end }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const muted = isDark ? alpha("#fff", 0.55) : alpha("#152A36", 0.55);
  const text = isDark ? alpha("#fff", 0.88) : "#152A36";
  const hoverBg = isDark ? alpha("#fff", 0.06) : alpha("#152A36", 0.05);
  const activeBg = isDark ? alpha(ACCENT, 0.16) : alpha("#152A36", 0.07);

  if (item.disabled) {
    return (
      <Tooltip title={!expanded ? item.label : "Em breve"} placement="right" enterDelay={350}>
        <Box>
          <ListItemButton
            disabled
            sx={{
              mx: expanded ? 1 : 0.75,
              px: expanded ? 1.25 : 1,
              py: 0.95,
              minHeight: 42,
              justifyContent: expanded ? "flex-start" : "center",
              borderRadius: 2.5,
              color: muted,
              "& .MuiListItemIcon-root": {
                color: muted,
                minWidth: expanded ? 34 : 0,
                justifyContent: "center",
              },
            }}
          >
            <ListItemIcon>
              <Icon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500, noWrap: true }}
              sx={{
                opacity: expanded ? 1 : 0,
                maxWidth: expanded ? 180 : 0,
                m: 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            />
          </ListItemButton>
        </Box>
      </Tooltip>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={Boolean(end)}
      style={{ textDecoration: "none", color: "inherit" }}
      onClick={() => onNavigate?.()}
    >
      {({ isActive }) => (
        <Tooltip title={!expanded ? item.label : ""} placement="right" enterDelay={350}>
          <Box>
            <ListItemButton
              selected={isActive}
              sx={{
                mx: expanded ? 1 : 0.75,
                px: expanded ? 1.25 : 1,
                py: 0.95,
                minHeight: 42,
                justifyContent: expanded ? "flex-start" : "center",
                borderRadius: 2.5,
                color: isActive ? (isDark ? "#fff" : "#152A36") : text,
                bgcolor: isActive ? activeBg : "transparent",
                transition: "background-color 0.18s ease, color 0.18s ease",
                "&:hover": {
                  bgcolor: isActive ? activeBg : hoverBg,
                },
                "&.Mui-selected:hover": {
                  bgcolor: activeBg,
                },
                "& .MuiListItemIcon-root": {
                  color: isActive ? ACCENT : muted,
                  minWidth: expanded ? 34 : 0,
                  justifyContent: "center",
                  transition: `min-width ${DRAWER_DURATION} ${DRAWER_EASE}`,
                },
              }}
            >
              <ListItemIcon>
                <Icon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 13.5,
                  fontWeight: isActive ? 700 : 500,
                  noWrap: true,
                }}
                sx={{
                  opacity: expanded ? 1 : 0,
                  maxWidth: expanded ? 180 : 0,
                  m: 0,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  transition: `opacity 180ms ${DRAWER_EASE}, max-width ${DRAWER_DURATION} ${DRAWER_EASE}`,
                  transitionDelay: expanded ? "50ms" : "0ms",
                }}
              />
            </ListItemButton>
          </Box>
        </Tooltip>
      )}
    </NavLink>
  );
};

const resolveNavItems = (navItems, navGroups) => {
  if (Array.isArray(navItems) && navItems.length > 0) return navItems;
  if (Array.isArray(navGroups)) return navGroups.flatMap((group) => group.items || []);
  return [];
};

const DrawerChrome = ({
  moduleTitle,
  navItems,
  navGroups,
  iconMap,
  expanded,
  pinnedCollapsed,
  onToggleCollapse,
  onNavigate,
  showCollapseToggle = true,
  getNavLinkEnd,
  mode = "light",
  toggleColorMode,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const [query, setQuery] = useState("");
  const IconMap = iconMap || {};
  const items = useMemo(
    () => resolveNavItems(navItems, navGroups),
    [navItems, navGroups]
  );

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        (item.shortLabel || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const goModules = () => {
    onNavigate?.();
    navigate("/modules");
  };

  const border = isDark ? alpha("#fff", 0.08) : alpha("#152A36", 0.08);
  const muted = isDark ? alpha("#fff", 0.5) : alpha("#152A36", 0.5);
  const text = isDark ? "#fff" : "#152A36";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          px: expanded ? 2 : 1,
          pt: 2,
          pb: 1.5,
          gap: 1.25,
          minHeight: 68,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: 1 }}>
          <BrandMark expanded={expanded} onClick={goModules} />
          <Box
            sx={{
              opacity: expanded ? 1 : 0,
              maxWidth: expanded ? 140 : 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              transition: `opacity 180ms ${DRAWER_EASE}, max-width ${DRAWER_DURATION} ${DRAWER_EASE}`,
              transitionDelay: expanded ? "50ms" : "0ms",
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: text, lineHeight: 1.15 }}>
              {moduleTitle}
            </Typography>
            <Typography sx={{ fontSize: 11, color: muted, fontWeight: 500 }}>
              Visão geral
            </Typography>
          </Box>
        </Box>

        {showCollapseToggle && (
          <Tooltip
            title={pinnedCollapsed ? "Manter expandido" : "Recolher menu"}
            placement="right"
          >
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              aria-label={pinnedCollapsed ? "Manter expandido" : "Recolher menu"}
              sx={{
                color: muted,
                bgcolor: isDark ? alpha("#fff", 0.06) : alpha("#152A36", 0.05),
                opacity: expanded ? 1 : 0,
                pointerEvents: expanded ? "auto" : "none",
                transition: `opacity 180ms ${DRAWER_EASE}`,
                "&:hover": {
                  bgcolor: isDark ? alpha("#fff", 0.1) : alpha("#152A36", 0.09),
                },
              }}
            >
              {pinnedCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          px: expanded ? 2 : 1,
          pb: 1.25,
          opacity: expanded ? 1 : 0,
          maxHeight: expanded ? 56 : 0,
          overflow: "hidden",
          transition: `opacity 180ms ${DRAWER_EASE}, max-height ${DRAWER_DURATION} ${DRAWER_EASE}`,
        }}
      >
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 18, color: muted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#152A36", 0.04),
              fontSize: 13.5,
              "& fieldset": { borderColor: border },
              "&:hover fieldset": { borderColor: alpha(ACCENT, 0.45) },
              "&.Mui-focused fieldset": { borderColor: ACCENT },
            },
          }}
        />
      </Box>

      <Box
        sx={{
          px: expanded ? 0.5 : 0.25,
          pb: 1.5,
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
          transition: `padding ${DRAWER_DURATION} ${DRAWER_EASE}`,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            bgcolor: isDark ? alpha("#fff", 0.15) : alpha("#152A36", 0.15),
          },
        }}
      >
        {filteredItems.map((item) => {
          const Icon = IconMap[item.icon] || AppsOutlinedIcon;
          return (
            <NavLeaf
              key={item.path}
              item={item}
              Icon={Icon}
              expanded={expanded}
              onNavigate={onNavigate}
              end={getNavLinkEnd?.(item)}
            />
          );
        })}
        {filteredItems.length === 0 && expanded && (
          <Typography
            variant="body2"
            sx={{ px: 2.5, py: 2, color: muted, fontSize: 13 }}
          >
            Nenhuma rotina encontrada.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          mt: "auto",
          px: expanded ? 2 : 1,
          py: 1.5,
          borderTop: `1px solid ${border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          gap: 1,
        }}
      >
        {expanded && (
          <Typography sx={{ fontSize: 12, color: muted, fontWeight: 500 }}>
            Aparência
          </Typography>
        )}
        <Tooltip title={mode === "dark" ? "Tema claro" : "Tema escuro"} placement="right">
          <IconButton
            onClick={() => toggleColorMode?.()}
            size="small"
            aria-label="Alternar tema"
            sx={{
              color: ACCENT,
              bgcolor: alpha(ACCENT, 0.12),
              border: `1px solid ${alpha(ACCENT, 0.35)}`,
              "&:hover": {
                bgcolor: alpha(ACCENT, 0.2),
              },
            }}
          >
            {mode === "dark" ? (
              <LightModeOutlinedIcon fontSize="small" />
            ) : (
              <DarkModeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
};

/**
 * Menu lateral compartilhado (Controladoria / Cadastros).
 */
const ModuleSideNav = ({
  moduleTitle,
  navItems,
  navGroups,
  iconMap,
  drawerWidth,
  drawerWidthCollapsed,
  expanded,
  pinnedCollapsed,
  onToggleCollapse,
  onHoverChange,
  mobileOpen = false,
  onMobileClose,
  getNavLinkEnd,
  mode = "light",
  toggleColorMode,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const width = expanded ? drawerWidth : drawerWidthCollapsed;
  const paperSx = {
    boxSizing: "border-box",
    color: isDark ? "#fff" : "#152A36",
    bgcolor: isDark ? "#0E1620" : "#FFFFFF",
    borderRight: `1px solid ${isDark ? alpha("#fff", 0.08) : alpha("#152A36", 0.08)}`,
    display: "flex",
    flexDirection: "column",
    backgroundImage: "none",
  };

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            ...paperSx,
            width: drawerWidth,
          },
        }}
      >
        <DrawerChrome
          moduleTitle={moduleTitle}
          navItems={navItems}
          navGroups={navGroups}
          iconMap={iconMap}
          expanded
          pinnedCollapsed={false}
          onToggleCollapse={() => {}}
          onNavigate={onMobileClose}
          showCollapseToggle={false}
          getNavLinkEnd={getNavLinkEnd}
          mode={mode}
          toggleColorMode={toggleColorMode}
        />
      </Drawer>

      <Drawer
        variant="permanent"
        onMouseEnter={() => onHoverChange?.(true)}
        onMouseLeave={() => onHoverChange?.(false)}
        sx={{
          display: { xs: "none", md: "block" },
          width,
          flexShrink: 0,
          whiteSpace: "nowrap",
          transition: `width ${DRAWER_DURATION} ${DRAWER_EASE}`,
          "& .MuiDrawer-paper": {
            ...paperSx,
            width,
            position: "relative",
            overflowX: "hidden",
            transition: `width ${DRAWER_DURATION} ${DRAWER_EASE}`,
          },
        }}
      >
        <DrawerChrome
          moduleTitle={moduleTitle}
          navItems={navItems}
          navGroups={navGroups}
          iconMap={iconMap}
          expanded={expanded}
          pinnedCollapsed={pinnedCollapsed}
          onToggleCollapse={onToggleCollapse}
          getNavLinkEnd={getNavLinkEnd}
          mode={mode}
          toggleColorMode={toggleColorMode}
        />
      </Drawer>
    </>
  );
};

export default ModuleSideNav;
